import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import SnagLinkPicker, {
  SnagOption,
  snagOptionFromNode,
} from '@/components/SnagLinkPicker'
import TenderLinkPicker, {
  TenderOption,
  tenderOptionFromNode,
} from '@/components/TenderLinkPicker'
import { toastr } from 'react-redux-toastr'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import MemberCheckboxPicker from '../../../components/MemberCheckboxPicker'
import { Plus, Trash2, LoaderCircle, X, Send, Info, ListChecks, Users, Link2 } from 'lucide-react'

import { useModuleActions } from '../../../hooks/useMyPermissions'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import AgendaListManager from './AgendaListManager'
import SendInvitationDialog from './SendInvitationDialog'
import {
  utcToDatetimeLocal,
  utcToDateInput,
  datetimeLocalToUtc,
  dateInputToUtc,
} from '../../../utils/timezone'

interface Props {
  isOpen: boolean
  onClose: (saved?: boolean) => void
  meetingId: string | null
  projectId: string
}

interface ExternalGuest {
  id?: string
  name: string
  email: string
  company: string
}

const DEFAULT_STATE = {
  title: '',
  description: '',
  type: 'PROGRESS' as string,
  scheduledAt: '',
  durationMinutes: 60,
  isVirtual: false,
  confidential: false,
  onlineProvider: 'NONE' as string,
  location: '',
  virtualLink: '',
  organizerId: '' as string,
  attendeeIds: [] as string[],
  externalGuests: [] as ExternalGuest[],
  linkedSnagIds: [] as string[],
  linkedDocIds: [] as string[],
  linkedTenderIds: [] as string[],
  recurrence: 'NONE' as string,
  recurrenceUntil: '' as string,
}

// toDatetimeLocalValue / toDateValue used to interpret the stored UTC ISO in
// the browser's local timezone — now we always render in the project's
// timezone via the centralised helpers. Imported below.

const MeetingModal: React.FC<Props> = ({ isOpen, onClose, meetingId, projectId }) => {
  const { t } = useTranslation()
  const [form, setForm] = useState(DEFAULT_STATE)
  const [submitting, setSubmitting] = useState(false)
  const [submittingWithInvite, setSubmittingWithInvite] = useState(false)
  const [pendingInvitationFor, setPendingInvitationFor] = useState<string | null>(null)
  const [pendingInvitationAlreadySent, setPendingInvitationAlreadySent] = useState(false)

  const { data: meData } = useQuery(Query.ME)
  const meConnected = !!meData?.me?.microsoftConnected
  const { canEdit, canDelete } = useModuleActions('MEETINGS', projectId)

  const { data: meetingData, loading: loadingMeeting } = useQuery(Query.MEETING, {
    variables: { id: meetingId },
    skip: !meetingId,
  })

  // Resolve the project's timezone so the datetime picker shows wall-clock
  // time at the project location regardless of the user's browser TZ.
  const { data: projectData } = useQuery(Query.PROJECT, {
    variables: { id: projectId },
    skip: !projectId,
  })
  const users = (projectData?.project?.users || []).map((pu: any) => ({
    id: pu.user.id,
    name: pu.user.name,
    email: pu.user.email,
  }))
  const projectTz: string | undefined =
    meetingData?.meeting?.project?.resolvedTimezone ||
    projectData?.project?.resolvedTimezone ||
    undefined

  const [createMeeting] = useMutation(Mutation.CREATE_MEETING)
  const [updateMeeting] = useMutation(Mutation.UPDATE_MEETING)
  const [setAttendees] = useMutation(Mutation.SET_MEETING_ATTENDEES)
  const [setExternalGuests] = useMutation(Mutation.SET_MEETING_EXTERNAL_GUESTS)
  const [setLinks] = useMutation(Mutation.SET_MEETING_LINKS)

  const [initialForm, setInitialForm] = useState<typeof DEFAULT_STATE>(DEFAULT_STATE)
  const [hydratedForId, setHydratedForId] = useState<string | null>(null)
  const [selectedSnagOptions, setSelectedSnagOptions] = useState<SnagOption[]>([])
  const [selectedTenderOptions, setSelectedTenderOptions] = useState<
    TenderOption[]
  >([])

  useEffect(() => {
    if (!isOpen) {
      setHydratedForId(null)
      return
    }
    if (meetingId && meetingData?.meeting && hydratedForId !== meetingId) {
      const m = meetingData.meeting
      const hydrated = {
        title: m.title || '',
        description: m.description || '',
        type: m.type,
        scheduledAt: utcToDatetimeLocal(m.scheduledAt, projectTz),
        durationMinutes: m.durationMinutes || 60,
        isVirtual: !!m.isVirtual,
        confidential: !!m.confidential,
        onlineProvider: m.onlineProvider || 'NONE',
        location: m.location || '',
        virtualLink: m.virtualLink || '',
        organizerId: m.organizer?.id || '',
        attendeeIds: (m.attendees || []).map((u: any) => u.id),
        externalGuests: Array.isArray(m.externalGuests)
          ? m.externalGuests.map((g: any) => ({
              id: g.id,
              name: g.name || '',
              email: g.email || '',
              company: g.company || '',
            }))
          : [],
        linkedSnagIds: (m.linkedSnags || []).map((s: any) => s.id),
        linkedDocIds: (m.linkedDocs || []).map((d: any) => d.id),
        linkedTenderIds: (m.linkedTenders || []).map((tn: any) => tn.id),
        recurrence: m.recurrence || 'NONE',
        recurrenceUntil: m.recurrenceUntil ? utcToDateInput(m.recurrenceUntil, projectTz) : '',
      }
      setForm(hydrated)
      setInitialForm(hydrated)
      setSelectedSnagOptions((m.linkedSnags || []).map(snagOptionFromNode))
      setSelectedTenderOptions(
        (m.linkedTenders || []).map(tenderOptionFromNode)
      )
      setHydratedForId(meetingId)
    } else if (!meetingId && hydratedForId !== 'new') {
      setForm(DEFAULT_STATE)
      setInitialForm(DEFAULT_STATE)
      setSelectedSnagOptions([])
      setSelectedTenderOptions([])
      setHydratedForId('new')
    }
  }, [isOpen, meetingId, meetingData, hydratedForId])

  const handleAddGuest = () => {
    setForm({
      ...form,
      externalGuests: [...form.externalGuests, { name: '', email: '', company: '' }],
    })
  }

  const handleRemoveGuest = (idx: number) => {
    setForm({
      ...form,
      externalGuests: form.externalGuests.filter((_, i) => i !== idx),
    })
  }

  const handleToggleId = (list: string[], id: string): string[] =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id]

  const allAttendeesSelected =
    users.length > 0 && users.every((u: any) => form.attendeeIds.includes(u.id))

  const handleToggleAllAttendees = () =>
    setForm({
      ...form,
      attendeeIds: allAttendeesSelected ? [] : users.map((u: any) => u.id),
    })

  const canSave = !!form.title && !!form.scheduledAt

  const buildInput = () => ({
    title: form.title,
    description: form.description || null,
    type: form.type,
    scheduledAt: datetimeLocalToUtc(form.scheduledAt, projectTz),
    durationMinutes: Number(form.durationMinutes) || 60,
    onlineProvider: form.onlineProvider,
    isVirtual: form.onlineProvider !== 'NONE',
    confidential: form.confidential,
    location: form.onlineProvider === 'NONE' ? (form.location || null) : null,
    virtualLink: form.onlineProvider === 'NONE' ? null : (form.virtualLink || null),
    organizerId: form.organizerId || null,
  })

  const handleSave = async (andInvite: boolean = false) => {
    if (!canSave) return
    if (andInvite) setSubmittingWithInvite(true)
    else setSubmitting(true)
    try {
      let savedId: string | null = meetingId
      let alreadySent = false
      if (meetingId) {
        const metaChanged =
          form.title !== initialForm.title ||
          form.description !== initialForm.description ||
          form.type !== initialForm.type ||
          form.scheduledAt !== initialForm.scheduledAt ||
          Number(form.durationMinutes) !== Number(initialForm.durationMinutes) ||
          form.isVirtual !== initialForm.isVirtual ||
          form.onlineProvider !== initialForm.onlineProvider ||
          form.location !== initialForm.location ||
          form.virtualLink !== initialForm.virtualLink ||
          form.organizerId !== initialForm.organizerId ||
          form.recurrence !== initialForm.recurrence ||
          form.recurrenceUntil !== initialForm.recurrenceUntil

        const sameSet = (a: string[], b: string[]) =>
          a.length === b.length && new Set(a).size === new Set([...a, ...b]).size

        const attendeesChanged = !sameSet(form.attendeeIds, initialForm.attendeeIds)
        const linksChanged =
          !sameSet(form.linkedSnagIds, initialForm.linkedSnagIds) ||
          !sameSet(form.linkedDocIds, initialForm.linkedDocIds) ||
          !sameSet(form.linkedTenderIds, initialForm.linkedTenderIds)
        const guestsChanged =
          JSON.stringify(form.externalGuests) !==
          JSON.stringify(initialForm.externalGuests)

        const meetingLevelWork: Promise<any>[] = []
        if (metaChanged) {
          meetingLevelWork.push(
            updateMeeting({
              variables: {
                id: meetingId,
                input: {
                  ...buildInput(),
                  recurrence: form.recurrence,
                  recurrenceUntil: form.recurrenceUntil
                    ? dateInputToUtc(form.recurrenceUntil, projectTz)
                    : null,
                },
              },
            })
          )
        }
        if (attendeesChanged) {
          meetingLevelWork.push(
            setAttendees({
              variables: { id: meetingId, attendeeIds: form.attendeeIds },
            })
          )
        }
        if (guestsChanged) {
          meetingLevelWork.push(
            setExternalGuests({
              variables: { id: meetingId, guests: form.externalGuests },
            })
          )
        }
        if (linksChanged) {
          meetingLevelWork.push(
            setLinks({
              variables: {
                id: meetingId,
                snagIds: form.linkedSnagIds,
                docIds: form.linkedDocIds,
                tenderIds: form.linkedTenderIds,
              },
            })
          )
        }

        await Promise.all(meetingLevelWork)
        alreadySent = !!meetingData?.meeting?.invitationsSentAt
      } else {
        const res = await createMeeting({
          variables: {
            input: {
              projectId,
              ...buildInput(),
              attendeeIds: form.attendeeIds,
              externalGuests: form.externalGuests,
              agendaItems: [],
              linkedSnagIds: form.linkedSnagIds,
              linkedDocIds: form.linkedDocIds,
              linkedTenderIds: form.linkedTenderIds,
              recurrence: form.recurrence,
              recurrenceUntil: form.recurrenceUntil
                ? dateInputToUtc(form.recurrenceUntil, projectTz)
                : null,
            },
          },
        })
        savedId = res.data?.createMeeting?.id ?? null
      }
      toastr.success(t('Common_success'), t('Meeting_saved'))
      if (andInvite && savedId) {
        setPendingInvitationAlreadySent(alreadySent)
        setPendingInvitationFor(savedId)
      } else {
        onClose(true)
      }
    } catch (error: any) {
      toastr.error(t('Common_error'), error.message || 'Unknown error')
    } finally {
      setSubmitting(false)
      setSubmittingWithInvite(false)
    }
  }

  const invitationBaselineEmails: string[] = Array.from(
    new Set(
      [
        users.find((u: any) => u.id === form.organizerId)?.email,
        ...form.attendeeIds.map((id) => users.find((u: any) => u.id === id)?.email),
        ...form.externalGuests.map((g) => g.email),
      ].filter(Boolean)
    )
  )

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent hideClose className="max-w-5xl h-[99vh] max-h-[99vh] flex flex-col overflow-hidden p-0 gap-0 sm:rounded-lg">
        <DialogClose className="absolute right-3 top-3 z-10 inline-flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none">
          <X className="h-5 w-5" />
          <span className="sr-only">{t('Common_close')}</span>
        </DialogClose>
        <Tabs defaultValue="basic" className="flex-1 min-h-0 flex flex-col">
          <DialogHeader className="shrink-0 border-b pl-6 pr-14 py-3 flex-row flex-wrap !space-y-0 items-center gap-x-4 gap-y-2">
            <DialogTitle className="flex-1 min-w-[240px] truncate">
              {meetingId ? t('Meeting_edit_title') : t('Meeting_new_title')}
            </DialogTitle>
            <TabsList className="shrink-0">
              <TabsTrigger value="basic" className="gap-1.5">
                <Info className="h-4 w-4" />
                {t('Meeting_modal_tab_basic')}
              </TabsTrigger>
              <TabsTrigger value="agenda" className="gap-1.5">
                <ListChecks className="h-4 w-4" />
                {t('Meeting_modal_tab_agenda')}
              </TabsTrigger>
              <TabsTrigger value="attendees" className="gap-1.5">
                <Users className="h-4 w-4" />
                {t('Meeting_modal_tab_attendees')}
              </TabsTrigger>
              <TabsTrigger value="links" className="gap-1.5">
                <Link2 className="h-4 w-4" />
                {t('Meeting_modal_tab_links')}
              </TabsTrigger>
            </TabsList>
          </DialogHeader>

          {loadingMeeting && meetingId ? (
            <div className="flex-1 flex items-center justify-center p-8 text-muted-foreground">
              <LoaderCircle className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
              {/* BASIC */}
              <TabsContent
                value="basic"
                className="mt-0 space-y-4"
              >
              <div className="space-y-2">
                <Label htmlFor="title">{t('Meeting_field_title')}</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">{t('Meeting_field_description')}</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('Meeting_field_type')}</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PROGRESS">{t('Meeting_type_progress')}</SelectItem>
                      <SelectItem value="SAFETY">{t('Meeting_type_safety')}</SelectItem>
                      <SelectItem value="CLIENT">{t('Meeting_type_client')}</SelectItem>
                      <SelectItem value="TECHNICAL">{t('Meeting_type_technical')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('Meeting_field_organizer')}</Label>
                  <Select
                    value={form.organizerId || ''}
                    onValueChange={(v) => setForm({ ...form, organizerId: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((u: any) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-start justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="confidential">{t('Meeting_field_confidential')}</Label>
                  <p className="text-xs text-muted-foreground">
                    {t('Meeting_field_confidential_hint')}
                  </p>
                </div>
                <Switch
                  id="confidential"
                  checked={form.confidential}
                  onCheckedChange={(v) => setForm({ ...form, confidential: v })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="scheduledAt">{t('Meeting_field_scheduled_at')}</Label>
                  <Input
                    id="scheduledAt"
                    type="datetime-local"
                    value={form.scheduledAt}
                    onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })}
                  />
                  {projectTz && (
                    <p className="text-xs text-muted-foreground">
                      {t('Meeting_field_scheduled_at_tz_hint', { tz: projectTz })}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duration">{t('Meeting_field_duration')}</Label>
                  <Input
                    id="duration"
                    type="number"
                    min={15}
                    step={15}
                    value={form.durationMinutes}
                    onChange={(e) =>
                      setForm({ ...form, durationMinutes: Number(e.target.value) })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('Meeting_field_online_provider')}</Label>
                <Select value={form.onlineProvider} onValueChange={(v) => setForm({ ...form, onlineProvider: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">{t('Meeting_provider_in_person')}</SelectItem>
                    <SelectItem value="TEAMS">{t('Meeting_provider_teams')}</SelectItem>
                    <SelectItem value="MANUAL">{t('Meeting_provider_manual')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.onlineProvider === 'NONE' && (
                <div className="space-y-2">
                  <Label htmlFor="location">{t('Meeting_field_location')}</Label>
                  <Input
                    id="location"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder={t('Meeting_field_location_placeholder')}
                  />
                </div>
              )}

              {form.onlineProvider === 'MANUAL' && (
                <div className="space-y-2">
                  <Label htmlFor="virtualLink">{t('Meeting_field_virtual_link')}</Label>
                  <Input
                    id="virtualLink"
                    value={form.virtualLink}
                    onChange={(e) => setForm({ ...form, virtualLink: e.target.value })}
                    placeholder="https://…"
                  />
                </div>
              )}

              {form.onlineProvider === 'TEAMS' && !meConnected && (
                <p className="text-sm text-amber-600">
                  {t('Meeting_teams_connect_hint')}{' '}
                  <a href="/account" className="underline">{t('Account_connect')}</a>
                </p>
              )}

              <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                <div className="space-y-2">
                  <Label>{t('Meeting_field_recurrence')}</Label>
                  <Select
                    value={form.recurrence}
                    onValueChange={(v) => setForm({ ...form, recurrence: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NONE">{t('Meeting_recurrence_none')}</SelectItem>
                      <SelectItem value="WEEKLY">{t('Meeting_recurrence_weekly')}</SelectItem>
                      <SelectItem value="BIWEEKLY">{t('Meeting_recurrence_biweekly')}</SelectItem>
                      <SelectItem value="MONTHLY">{t('Meeting_recurrence_monthly')}</SelectItem>
                      <SelectItem value="QUARTERLY">{t('Meeting_recurrence_quarterly')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">{t('Meeting_recurrence_helper')}</p>
                </div>
                {form.recurrence !== 'NONE' && (
                  <div className="space-y-2">
                    <Label htmlFor="recurrenceUntil">{t('Meeting_field_recurrence_until')}</Label>
                    <Input
                      id="recurrenceUntil"
                      type="date"
                      value={form.recurrenceUntil}
                      onChange={(e) => setForm({ ...form, recurrenceUntil: e.target.value })}
                    />
                  </div>
                )}
              </div>
            </TabsContent>

            {/* AGENDA */}
            <TabsContent
              value="agenda"
              className="mt-0 space-y-3"
            >
              {meetingId ? (
                <AgendaListManager
                  meetingId={meetingId}
                  agendaItems={meetingData?.meeting?.agendaItems || []}
                  users={users}
                  canEdit={canEdit}
                  canDelete={canDelete}
                />
              ) : (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  {t('Meeting_agenda_save_first_hint')}
                </p>
              )}
            </TabsContent>

            {/* ATTENDEES */}
            <TabsContent
              value="attendees"
              className="mt-0 space-y-4"
            >
              <div>
                <MemberCheckboxPicker
                  members={users}
                  selectedIds={form.attendeeIds}
                  onToggle={(id) =>
                    setForm({ ...form, attendeeIds: handleToggleId(form.attendeeIds, id) })
                  }
                  onToggleAll={handleToggleAllAttendees}
                  allSelected={allAttendeesSelected}
                  label={t('Meeting_attendees_internal')}
                  showCounter
                  maxHeightClassName="max-h-[240px]"
                />
                <div className="flex flex-wrap gap-1 mt-2">
                  {form.attendeeIds.map((id) => {
                    const u = users.find((x: any) => x.id === id)
                    if (!u) return null
                    return (
                      <Badge key={id} variant="outline" className="gap-1">
                        {u.name}
                        <X
                          className="h-3 w-3 cursor-pointer"
                          onClick={() =>
                            setForm({
                              ...form,
                              attendeeIds: form.attendeeIds.filter((x) => x !== id),
                            })
                          }
                        />
                      </Badge>
                    )
                  })}
                </div>
              </div>

              <div>
                <Label className="block mb-2">{t('Meeting_attendees_external')}</Label>
                {form.externalGuests.length === 0 && (
                  <p className="text-xs text-muted-foreground mb-2">
                    {t('Meeting_attendees_external_empty')}
                  </p>
                )}
                {form.externalGuests.map((g, idx) => (
                  <div key={idx} className="flex items-center gap-2 mb-2">
                    <Input
                      placeholder={t('Meeting_guest_name')}
                      value={g.name}
                      onChange={(e) => {
                        const next = [...form.externalGuests]
                        next[idx] = { ...g, name: e.target.value }
                        setForm({ ...form, externalGuests: next })
                      }}
                    />
                    <Input
                      placeholder={t('Meeting_guest_email')}
                      value={g.email}
                      onChange={(e) => {
                        const next = [...form.externalGuests]
                        next[idx] = { ...g, email: e.target.value }
                        setForm({ ...form, externalGuests: next })
                      }}
                    />
                    <Input
                      placeholder={t('Meeting_guest_company')}
                      value={g.company}
                      onChange={(e) => {
                        const next = [...form.externalGuests]
                        next[idx] = { ...g, company: e.target.value }
                        setForm({ ...form, externalGuests: next })
                      }}
                    />
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveGuest(idx)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={handleAddGuest}>
                  <Plus className="h-4 w-4" />
                  {t('Meeting_guest_add')}
                </Button>
              </div>
            </TabsContent>

            {/* LINKS */}
            <TabsContent
              value="links"
              className="mt-0 space-y-4"
            >
              <div>
                <Label className="block mb-2">{t('Meeting_links_snags')}</Label>
                <SnagLinkPicker
                  isMulti
                  projectId={projectId}
                  value={selectedSnagOptions}
                  onChange={(next) => {
                    setSelectedSnagOptions(next)
                    setForm({
                      ...form,
                      linkedSnagIds: next.map((o) => o.value),
                    })
                  }}
                />
              </div>

              <div>
                <Label className="block mb-2">{t('Meeting_links_tenders')}</Label>
                <TenderLinkPicker
                  projectId={projectId}
                  value={selectedTenderOptions}
                  onChange={(next) => {
                    setSelectedTenderOptions(next)
                    setForm({
                      ...form,
                      linkedTenderIds: next.map((o) => o.value),
                    })
                  }}
                />
              </div>

              <p className="text-xs text-muted-foreground">{t('Meeting_links_docs_note')}</p>
            </TabsContent>
            </div>
          )}
        </Tabs>

        <div className="shrink-0 flex items-center justify-end gap-2 px-6 py-3 border-t">
          <Button
            variant="outline"
            onClick={() => onClose()}
            disabled={submitting || submittingWithInvite}
          >
            {t('Common_cancel')}
          </Button>
          <Button
            variant={meetingId ? 'default' : 'outline'}
            onClick={() => handleSave(false)}
            disabled={!canSave || submitting || submittingWithInvite}
          >
            {submitting && <LoaderCircle className="h-4 w-4 mr-2 animate-spin" />}
            {t('Common_save')}
          </Button>
          {!meetingId && (
            <Button
              onClick={() => handleSave(true)}
              disabled={!canSave || submitting || submittingWithInvite}
            >
              {submittingWithInvite ? (
                <LoaderCircle className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {t('Meeting_save_and_invite')}
            </Button>
          )}
        </div>
      </DialogContent>

    </Dialog>
    {pendingInvitationFor && (
      <SendInvitationDialog
        isOpen={!!pendingInvitationFor}
        onClose={() => {
          setPendingInvitationFor(null)
          setPendingInvitationAlreadySent(false)
          onClose(true)
        }}
        meetingId={pendingInvitationFor}
        baselineEmails={invitationBaselineEmails}
        alreadySent={pendingInvitationAlreadySent}
      />
    )}
    </>
  )
}

export default MeetingModal
